import styled from 'styled-components';
import { primitives } from '@veneer/primitives';
import { Card } from '@veneer/core';

const { color, typography, layout } = primitives;

export interface CardWithDisableProps {
  $disabled?: boolean;
  $suspendedORExpired?: boolean;
  $overDueORAboutToExpire?: boolean;
}

export const CardWithDisable = styled(Card)<CardWithDisableProps>`
  ${({ $disabled }) =>
    $disabled
      ? `
        pointer-events: none;
        color: ${color.gray6} !important;

        * {
          color: ${color.gray6} !important;
        }
      `
      : null}
`;

export const StaticHeightCard = styled(CardWithDisable)<CardWithDisableProps>`
  box-shadow: rgba(33, 33, 33, 0.2) 0px 1px 4px 1px;
  min-height: 262px !important;
  padding: ${layout.size4} !important;
`;

export const CardLinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  line-height: 20px;
  cursor: pointer;
`;

export const Container = styled.div`
  padding: 40px;
`;

export const StyledBreadcrumbs = styled.p`
  font-family: ${typography.family0};
  color: ${color.gray12};
  margin-bottom: 10px;
`;

export const MainTitle = styled.h1`
  font-family: ${typography.family0};
  color: ${color.gray12};
  font-size: ${typography.size7};
  line-height: ${typography.lineHeight5};
  font-weight: ${typography.weight4};
  margin-bottom: ${layout.size8}px;

  @media (min-width: ${layout.breakpoint1}px) {
    font-size: ${typography.size5};
  }
`;

export const Subtitle = styled.p`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size3};
  line-height: ${typography.lineHeight3};
  margin-bottom: 40px;
`;

export const ReviewSection = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const LeftColumn = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const RightColumn = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const PlanSummary = styled.div`
  margin-bottom: 40px;
`;

export const SectionTitle = styled.h2`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size5};
  line-height: ${typography.lineHeight5};
  font-weight: ${typography.weight3};
  margin-bottom: 20px;
`;

export const PlanCard = styled.div`
  border: 1px solid ${color.gray3};
  border-radius: 8px;
  padding: 20px;
  background-color: ${color.white};
`;

export const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

export const PlanIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: ${color.electricBlue1};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: ${color.electricBlue7};
`;

export const PlanDetails = styled.div`
  flex: 1;
`;

export const PlanName = styled.h3`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size4};
  font-weight: ${typography.weight3};
  margin: 0 0 4px 0;
`;

export const PlanPages = styled.p`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size2};
  margin: 0;
`;

export const PlanPrice = styled.div`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size4};
  font-weight: ${typography.weight3};
  text-align: right;
`;

export const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
`;

export const PlanFeature = styled.li`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size2};
  line-height: ${typography.lineHeight2};
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;

  &::before {
    content: '•';
    color: ${color.electricBlue7};
    position: absolute;
    left: 0;
  }
`;

export const PriceBreakdown = styled.div`
  border: 1px solid ${color.gray3};
  border-radius: 8px;
  padding: 20px;
  background-color: ${color.white};
  margin-bottom: 20px;
`;

export const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid ${color.gray2};
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

export const PriceLabel = styled.span`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size2};
  font-weight: ${typography.weight3};
  min-width: 140px;
  flex-shrink: 0;
`;

export const PriceValue = styled.span`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size2};
  text-align: right;
  flex: 1;
`;

export const TotalLabel = styled.span`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size4};
  font-weight: ${typography.weight3};
`;

export const TotalValue = styled.span`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size4};
  font-weight: ${typography.weight3};
`;

export const PaymentSection = styled.div`
  margin-bottom: 40px;
`;

export const PaymentCard = styled.div`
  border: 1px solid ${color.gray3};
  border-radius: 8px;
  padding: 20px;
  background-color: ${color.white};
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const PaymentMethod = styled.div`
  flex: 1;
`;

export const ShippingAddress = styled.div`
  flex: 1;
`;

export const FieldLabel = styled.h4`
  font-family: ${typography.family0};
  color: ${color.black};
  font-size: ${typography.size3};
  font-weight: ${typography.weight3};
  margin: 0 0 12px 0;
`;

export const PaymentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

export const CardBrand = styled.div`
  width: 40px;
  height: 24px;
  background-color: ${color.electricBlue7};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.white};
  font-size: 10px;
  font-weight: bold;
`;

export const CardDetails = styled.div`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size2};
  margin-bottom: 4px;
`;

export const AddressText = styled.p`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size2};
  line-height: ${typography.lineHeight2};
  margin: 0 0 4px 0;
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${color.electricBlue7};
  font-family: ${typography.family0};
  font-size: ${typography.size2};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;

  &:hover {
    color: ${color.electricBlue8};
  }
`;

export const TermsSection = styled.div`
  margin-bottom: 20px;
`;

export const TermsCard = styled.div`
  border: 1px solid ${color.gray3};
  border-radius: 8px;
  padding: 20px;
  background-color: ${color.white};
`;

export const TermsContent = styled.div`
  font-family: ${typography.family0};
  color: ${color.gray7};
  font-size: ${typography.size2};
  line-height: ${typography.lineHeight3};
  margin-bottom: 16px;
`;

export const PlanBadge = styled.div`
  background-color: ${color.green2};
  color: ${color.green8};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: ${typography.size1};
  font-weight: ${typography.weight3};
  margin-left: 8px;
`;
export const PaymentShippingDetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
